import React from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import EventFormModal from 'r/ui/EventFormModal';
import {
  fetchApiRaw,
  GET_ANALYSES_CMS,
  GET_EVENTS_PLAY,
} from 'r/utils/fetchApiRaw';
import type { Analyse, SanseiEvent } from 'r/types';

export default function AnalysisPlayEventRoute() {
  const { analysisId } = useParams<{ analysisId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // --- Analysis ---
  const cachedAnalysis = queryClient
    .getQueriesData<{ data: Analyse[] }>({ queryKey: ['cms-analyses'] })
    .flatMap(([, page]) => page?.data ?? [])
    .find((a) => a.id === analysisId);

  const { data: fetchedAnalysis } = useQuery({
    queryKey: ['cms-analysis', analysisId],
    queryFn: async () => {
      const res = await fetchApiRaw(GET_ANALYSES_CMS, {
        queryParams: {
          filter: encodeURIComponent(`"id":["${analysisId}"]`),
          take: '1',
        },
      });
      const data = Array.isArray(res.data) ? res.data : [];
      return (data[0] as Analyse) ?? null;
    },
    enabled: !cachedAnalysis && !!analysisId,
    staleTime: 5 * 60 * 1000,
  });

  const analysis = cachedAnalysis ?? fetchedAnalysis ?? undefined;
  const tweetID = analysis?.tweetID;

  // --- Existing play event ---
  const cachedEvent =
    tweetID ?
      (queryClient
        .getQueriesData<Analyse[]>({ queryKey: ['cms-events-play'] })
        .flatMap(([, data]) => (Array.isArray(data) ? data : []))
        .find((e) => e.tweetID === tweetID) as SanseiEvent | undefined)
    : undefined;

  const { data: fetchedEvent } = useQuery({
    queryKey: ['play-event-by-tweet', tweetID],
    queryFn: async () => {
      const res = await fetchApiRaw(GET_EVENTS_PLAY, {
        queryParams: {
          filter: encodeURIComponent(`"tweetID":["${tweetID}"]`),
          include: 'eventOutcomes',
          take: '1',
        },
      });
      const data = Array.isArray(res.data) ? res.data : [];
      return (data[0] as SanseiEvent) ?? null;
    },
    enabled: !cachedEvent && !!tweetID,
    staleTime: 5 * 60 * 1000,
  });

  const existingEvent = cachedEvent ?? fetchedEvent ?? undefined;

  const handleClose = () => {
    if (location.state?.background) {
      navigate(-1);
    } else {
      navigate('/?tab=analyses');
    }
  };

  if (!analysis) return null;

  return (
    <EventFormModal
      mode={existingEvent ? 'edit' : 'create'}
      analysis={analysis}
      event={
        existingEvent as React.ComponentProps<typeof EventFormModal>['event']
      }
      isOpen={true}
      onClose={handleClose}
      onSuccess={() => {}}
    />
  );
}
